// src/services/storeDetailsService.js
import { api } from "./interceptors";

// Helper function to format time from API format (HH:mm:ss) to 12-hour format
const formatTimeFromAPI = (timeString) => {
  try {
    if (!timeString) return "12:00 PM";

    // Handle different time formats from API
    let time = timeString;
    if (timeString.includes(".")) {
      // Format: "13:57:53.772784" - extract just the time part
      time = timeString.split(".")[0];
    }

    // Parse the time string
    const [hours, minutes] = time.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0);

    // Format to 12-hour format with AM/PM
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (error) {
    console.error("Error formatting time:", error);
    return timeString; // Fallback to original string
  }
};

/**
 * Store Details Service - Handles store detail API calls
 */
const storeDetailsService = {
  /**
   * Get detailed information about a specific store
   * @param {string} storeId - The store's public_id
   * @param {number} latitude - User's latitude for distance calculation
   * @param {number} longitude - User's longitude for distance calculation
   * @returns {Promise<Object>} - Returns store details in original API format
   */
  getStoreDetails: async (storeId, latitude = null, longitude = null) => {
    try {
      // Try to fetch from API first
      const params = {};
      if (latitude && longitude) {
        params.latitude = latitude.toFixed(8);
        params.longitude = longitude.toFixed(8);
      }

      const response = await api.get(`/stores/${storeId}/detail/`, { params });

      // Return API response as-is, no transformation needed
      return response.data;
    } catch (error) {
      console.error("Error fetching store details from API:", error);

    }
  },

  /**
   * Get surprise bags for a specific store
   * @param {string} storeId - The store's public_id
   * @returns {Promise<Array>} - Returns available surprise bags
   */
  getStoreSurpriseBags: async (storeId) => {
    try {
      const response = await api.get(`/stores/${storeId}/surprise-bags`);
      return response.data;
    } catch (error) {
      console.error("Error fetching surprise bags from API:", error);
      return [];
    }
  },

  /**
   * Get store reviews and ratings
   * @param {string} storeId - The store's public_id
   * @returns {Promise<Array>} - Returns store reviews
   */
  getStoreReviews: async (storeId) => {
    try {
      const response = await api.get(`/stores/${storeId}/reviews`);
      return response.data;
    } catch (error) {
      console.error("Error fetching store reviews from API:", error);

      // Return dummy reviews for development
      const dummyReviews = [
        {
          id: "review_1",
          userName: "Sarah M.",
          rating: 5,
          comment: "Amazing food and great value! The surprise bag was full of delicious treats.",
          date: "2024-01-15",
          verified: true,
        },
        {
          id: "review_2",
          userName: "John D.",
          rating: 4,
          comment: "Good quality food, perfect for reducing waste. Will definitely order again.",
          date: "2024-01-10",
          verified: true,
        },
      ];

      await new Promise((resolve) => setTimeout(resolve, 600));
      return dummyReviews;
    }
  },

  /**
   * Add store to favorites
   * @param {string} storeId - The store's public_id
   * @returns {Promise<Object>} - Returns success status
   */
  addToFavorites: async (storeId) => {
    try {
      const response = await api.post(`/stores/${storeId}/favorite`);
      return response.data;
    } catch (error) {
      console.error("Error adding store to favorites:", error);

      // Return dummy success for development
      await new Promise((resolve) => setTimeout(resolve, 300));
      return { success: true, message: "Store added to favorites" };
    }
  },

  /**
   * Remove store from favorites
   * @param {string} storeId - The store's public_id
   * @returns {Promise<Object>} - Returns success status
   */
  removeFromFavorites: async (storeId) => {
    try {
      const response = await api.delete(`/stores/${storeId}/favorite`);
      return response.data;
    } catch (error) {
      console.error("Error removing store from favorites:", error);

      // Return dummy success for development
      await new Promise((resolve) => setTimeout(resolve, 300));
      return { success: true, message: "Store removed from favorites" };
    }
  },

  /**
   * Check if store is currently open
   * @param {string} storeId - The store's public_id
   * @returns {Promise<Object>} - Returns current open status
   */
  isStoreOpen: async (storeId) => {
    try {
      const response = await api.get(`/stores/${storeId}/status`);
      return response.data;
    } catch (error) {
      console.error("Error checking store status from API:", error);

      // Simple logic for dummy data
      const now = new Date();
      const currentHour = now.getHours();

      // Assume most stores are open between 8 AM and 9 PM
      const isOpen = currentHour >= 8 && currentHour < 21;

      await new Promise((resolve) => setTimeout(resolve, 200));
      return {
        isOpen,
        nextStatusChange: isOpen ? "Closes at 9:00 PM" : "Opens at 8:00 AM",
        currentTime: now.toISOString(),
      };
    }
  },

  /**
   * Format time from API format for display
   * @param {string} timeString - Time in HH:mm:ss format
   * @returns {string} - Formatted time in 12-hour format
   */
  formatTime: formatTimeFromAPI,
};

export default storeDetailsService;